import { useEffect, useRef, useState } from "react";
import { Button, Container, Modal, Nav, Navbar } from "react-bootstrap";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { adminLogout, trackAnalyticsEvent } from "../services/api";
// import helpingSoldiers from "../constants/images/helping_soldiers.svg";
import airforceSeal from "../constants/images/airforceSeal.webp";
import armySeal from "../constants/images/armySeal.webp";
import coastGuardSeal from "../constants/images/coastGuardSeal.webp";
import helpingSoldiersBlue from "../constants/images/helping_soldiers_blue.svg";
import marineSeal from "../constants/images/marineSeal.webp";
import nationalGuardSeal from "../constants/images/nationalGuardSeal.PNG";
import navySeal from "../constants/images/navySeal.webp";
// import ThemeSwitcher from "./ThemeSwitcher";

const INSTALL_DISMISSED_KEY = "fvg-install-dismissed-v1";
const VISITOR_ID_KEY = "fvg-visitor-id-v1";

const militarySeals = [
  { name: "Army", image: armySeal },
  { name: "Marines", image: marineSeal },
  { name: "Navy", image: navySeal },
  { name: "Air Force", image: airforceSeal },
  { name: "Coast Guard", image: coastGuardSeal },
  { name: "National Guard", image: nationalGuardSeal },
];

const isIosSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isWebKit = /WebKit/.test(ua);
  const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isWebKit && !isOtherIOSBrowser;
};

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false;
  const mediaStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
};

const getDisplayMode = () => (isStandaloneMode() ? "standalone" : "browser");

const getOrCreateVisitorId = () => {
  const existing = localStorage.getItem(VISITOR_ID_KEY);
  if (existing) return existing;
  const next = `v_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  localStorage.setItem(VISITOR_ID_KEY, next);
  return next;
};

function SiteLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mainRef = useRef(null);
  const appOpenLoggedRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installMode, setInstallMode] = useState("none");
  const [showInstallHelpModal, setShowInstallHelpModal] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showFooterInstallLink, setShowFooterInstallLink] = useState(false);
  const [visitorId] = useState(() => getOrCreateVisitorId());
  const isAdminAuthed = localStorage.getItem("fvg-admin-auth") === "true";

  useEffect(() => {
    mainRef.current?.focus();
    setExpanded(false);
    trackAnalyticsEvent({
      eventType: "page_visit",
      eventName: "route_change",
      pagePath: pathname,
      referrerUrl: document.referrer || "",
      displayMode: getDisplayMode(),
      isAppInstallable: Boolean(deferredInstallPrompt),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      visitorId,
    }).catch(() => {});
  }, [pathname, deferredInstallPrompt, visitorId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const dismissed = localStorage.getItem(INSTALL_DISMISSED_KEY) === "1";
    const isMobile = window.matchMedia("(max-width: 991.98px)").matches;

    if (!isMobile || dismissed || isStandaloneMode()) {
      setShowInstallPrompt(false);
      setInstallMode("none");
    } else if (isIosSafari()) {
      setInstallMode("ios");
      setShowInstallPrompt(true);
    }

    if (isStandaloneMode() && !appOpenLoggedRef.current) {
      appOpenLoggedRef.current = true;
      trackAnalyticsEvent({
        eventType: "app_open",
        eventName: "standalone_launch",
        pagePath: pathname,
        displayMode: "standalone",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        visitorId,
      }).catch(() => {});
    }

    const handleBeforeInstallPrompt = (event) => {
      if (dismissed || isStandaloneMode() || !window.matchMedia("(max-width: 991.98px)").matches) return;
      event.preventDefault();
      setDeferredInstallPrompt(event);
      setInstallMode("android");
      setShowInstallPrompt(true);
      trackAnalyticsEvent({
        eventType: "app_install_prompt",
        eventName: "beforeinstallprompt",
        pagePath: pathname,
        displayMode: getDisplayMode(),
        isAppInstallable: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        visitorId,
      }).catch(() => {});
    };

    const handleAppInstalled = () => {
      localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
      setDeferredInstallPrompt(null);
      setInstallMode("none");
      setShowInstallPrompt(false);
      setShowInstallHelpModal(false);
      trackAnalyticsEvent({
        eventType: "app_installed",
        eventName: "appinstalled",
        pagePath: pathname,
        displayMode: getDisplayMode(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        visitorId,
      }).catch(() => {});
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [pathname, visitorId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 991.98px)");
    const syncVisibility = () => {
      setShowFooterInstallLink(mediaQuery.matches && !isStandaloneMode());
    };

    syncVisibility();
    mediaQuery.addEventListener("change", syncVisibility);
    window.addEventListener("orientationchange", syncVisibility);

    return () => {
      mediaQuery.removeEventListener("change", syncVisibility);
      window.removeEventListener("orientationchange", syncVisibility);
    };
  }, []);

  const handleAdminAction = async () => {
    if (!isAdminAuthed) {
      navigate("/admin/login");
      return;
    }
    await adminLogout();
    navigate("/");
  };

  const dismissInstallPrompt = () => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
    setShowInstallPrompt(false);
  };

  const handleInstallAction = async () => {
    if (installMode === "android" && deferredInstallPrompt) {
      trackAnalyticsEvent({
        eventType: "app_install_prompt",
        eventName: "install_button_tap",
        pagePath: pathname,
        displayMode: getDisplayMode(),
        isAppInstallable: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        visitorId,
      }).catch(() => {});
      deferredInstallPrompt.prompt();
      let outcome = "";
      try {
        const result = await deferredInstallPrompt.userChoice;
        outcome = String(result?.outcome || "");
      } catch {
        // Ignore prompt cancellation; user may install later.
      }

      if (outcome === "accepted") {
        setDeferredInstallPrompt(null);
        dismissInstallPrompt();
        return;
      }

      // If dismissed, keep the persistent footer link useful by showing manual steps.
      setShowInstallHelpModal(true);
      return;
    }
    setShowInstallHelpModal(true);
  };

  return (
    <div className="page-shell global-flag-bg" style={{ "--home-flag-bg": "url(/regUSFlag.svg)" }}>
      <Navbar
        expand="lg"
        fixed="top"
        className="fvg-nav"
        variant="light"
        aria-label="Primary navigation"
        expanded={expanded}
        onToggle={(nextExpanded) => setExpanded(Boolean(nextExpanded))}
      >
        <Container>
          <Navbar.Brand
            as={NavLink}
            to="/"
            className="fvg-brand"
            onClick={() => setExpanded(false)}
          >
            {/* <img
              src="/logo.svg"
              alt="Fremont Veterans Group logo"
              className="fvg-brand-logo"
            /> */}
            <img
              src={helpingSoldiersBlue}
              alt=""
              aria-hidden="true"
              className="fvg-brand-logo"
            />
            <span className="fvg-brand-text">FREMONT VETERANS GROUP</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="fvg-nav" />
          <Navbar.Collapse id="fvg-nav">
            <Nav className="ms-auto align-items-lg-center gap-lg-2">
              <Nav.Link
                as={NavLink}
                to="/"
                onClick={() => setExpanded(false)}
              >
                Home
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/about"
                onClick={() => setExpanded(false)}
              >
                About
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/programs"
                onClick={() => setExpanded(false)}
              >
                <span className="fvg-nav-two-line">
                  <span>Programs &</span>
                  <span>Resources</span>
                </span>
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/events"
                onClick={() => setExpanded(false)}
              >
                Events
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/mission-supporters"
                onClick={() => setExpanded(false)}
              >
                <span className="fvg-nav-two-line">
                  <span>Mission</span>
                  <span>Supporters</span>
                </span>
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/contact"
                onClick={() => setExpanded(false)}
              >
                <span className="fvg-nav-two-line">
                  <span>Contact &</span>
                  <span>More</span>
                </span>
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/support"
                className="fvg-donate-nav-link"
                onClick={() => setExpanded(false)}
              >
                Donate
              </Nav.Link>
              {/* {isAdminAuthed && (
                <Nav.Link
                  as={NavLink}
                  to="/admin"
                  className="text-warning"
                  onClick={() => setExpanded(false)}
                >
                  Admin
                </Nav.Link>
              )} */}
              {/* <ThemeSwitcher /> */}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main
        className="main-container"
        id="main-content"
        tabIndex={-1}
        ref={mainRef}
      >
        <Container fluid="xxl">
          <Outlet />
        </Container>
      </main>

      <footer className="fvg-footer">
        <Container>
          <div className="footer-branch-seals" aria-label="United States military service branches">
            {militarySeals.map((branch) => (
              <div key={branch.name} className="footer-branch-seal-item">
                <img src={branch.image} alt={`${branch.name} seal`} className="footer-branch-seal-img" />
                <span>{branch.name}</span>
              </div>
            ))}
          </div>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div className="d-flex flex-column">
              <small>Fremont Veterans Group is a registered nonprofit organization focused on veteran and family support services.</small>
              <small>Information on this site is provided as general community guidance and may change without notice.</small>
            </div>
            <div className="d-flex gap-2">
              {showFooterInstallLink && (
                <button
                  type="button"
                  className="fvg-admin-text-link fvg-install-footer-link"
                  onClick={handleInstallAction}
                >
                  Add as App
                </button>
              )}
              <NavLink
                to="/admin"
                className="fvg-admin-text-link"
              >
                Admin
              </NavLink>
              <button
                type="button"
                className={`fvg-admin-text-link fvg-admin-auth-btn ${isAdminAuthed ? "is-authed" : ""}`}
                onClick={handleAdminAction}
              >
                {isAdminAuthed ? "Admin Logout" : "Admin Login"}
              </button>
            </div>
          </div>
        </Container>
      </footer>

      {showInstallPrompt && (
        <div className="fvg-install-banner" role="dialog" aria-label="Add app to home screen">
          <div className="fvg-install-copy">
            <div className="fvg-install-title">Add as App</div>
            <div className="fvg-install-subtitle">
              {installMode === "ios"
                ? "Save this site to your home screen for quick one-tap access."
                : "Install for a faster home-screen experience."}
            </div>
          </div>
          <div className="fvg-install-actions">
            <button type="button" className="btn btn-sm btn-warning" onClick={handleInstallAction}>
              {installMode === "ios" ? "How To" : "Install"}
            </button>
            <button type="button" className="fvg-install-close" onClick={dismissInstallPrompt} aria-label="Dismiss add app prompt">
              x
            </button>
          </div>
        </div>
      )}

      <Modal show={showInstallHelpModal} onHide={() => setShowInstallHelpModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add as App</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {installMode === "ios" ? (
            <ol className="mb-0">
              <li>Tap the Share button in Safari.</li>
              <li>Open More, then choose Add to Home Screen.</li>
              <li>Tap Add to finish.</li>
            </ol>
          ) : (
            <ol className="mb-0">
              <li>Tap Install when prompted by your browser.</li>
              <li>If no prompt appears, open your browser menu.</li>
              <li>Select Install App or Add to Home Screen.</li>
            </ol>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInstallHelpModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SiteLayout;
